import { useState, useRef, useCallback } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import PageHeader from '@/components/settings/PageHeader';
import { ChevronDown } from 'lucide-react';
import CustomTextarea from '@/components/shared/molecules/CustomTextarea';
import { Document } from '@/icons/document';
import { TrashCan } from '@/icons/trash-can';
import { Button } from '@/components/shared/atoms/Button';
import { Attachment } from '@/icons/attachment';
import { FeedbackTypeBottomSheet } from '@/components/settings/bottom-sheets/FeedbackType';
import { useGetFeedbackAttachmentPresignUrl } from '@/hooks/api/useUploads';
import { useSubmitFeedback } from '@/hooks/api/useFeedback';
import { uploadFileToUrl } from '@wtf/shared';
import { useToast } from '@/components/shared/toast/Toast';
import { useImageUpload } from '@/hooks/useImageUpload';
import { ImageUploadType } from '@wtf/shared';
import { ChooseFileBottomSheet } from '@/components/shared/bottom-sheets/ChooseFileBottomSheet';
import { ImageCropperBottomSheet } from '@/components/shared/bottom-sheets/ImageCropperBottomSheet';
import { classNames } from '@/utils/classNames';

export const Route = createFileRoute('/settings/feedback')({
    component: RouteComponent,
})

function RouteComponent() {
    return <FeedbackPage />
}

// Feedback types for dropdown
const FEEDBACK_TYPES = [
    'Request new feature',
    'Report a bug',
    'Suggestion',
    'Compliment',
    'Other',
]

type SelectedFile = {
    id: string;
    name: string;
    type: string;
    file: File;
};

export const FeedbackPage = ({
    asChildren = false,
    rootClassNames,
}: {
    asChildren?: boolean
    rootClassNames?: string
}) => {
  const [isFeedbackTypeBottomSheetOpen, setIsFeedbackTypeBottomSheetOpen] =
    useState<boolean>(false);
  const [feedbackType, setFeedbackType] = useState<string>(FEEDBACK_TYPES[0]);
  const [feedbackDescription, setFeedbackDescription] = useState<string>("");
  const [selectedFiles, setSelectedFiles] = useState<SelectedFile[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [attachedImageUrls, setAttachedImageUrls] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Hooks for upload and submission
  const getPresignUrlMutation = useGetFeedbackAttachmentPresignUrl();
  const submitFeedbackMutation = useSubmitFeedback();
  const { addToast } = useToast();

  // Image upload for feedback attachments - no cropping required
  const imageUpload = useImageUpload({
    type: ImageUploadType.FEEDBACK_IMAGE,
    customCropping: { required: false }, // Disable cropping for feedback images
    onSuccess: (url) => {
      setAttachedImageUrls(prev => [...prev, url]);
      addToast('Image attached successfully', 'success');
    },
    onError: (error) => addToast(error, 'error')
  });

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const newFiles: SelectedFile[] = Array.from(e.target.files).map(
                (file) => ({
                    id: Math.random().toString(36).substring(2, 11),
                    name: file.name,
                    type: file.type,
                    file: file,
                })
            )

            setSelectedFiles((prev) => [...prev, ...newFiles])
        }
    }

    const removeFile = (id: string) => {
        setSelectedFiles((prev) => prev.filter((file) => file.id !== id))
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setIsSubmitting(true)

    try {
      // Upload files first if any are selected
      const uploadedUrls: string[] = [];

      if (selectedFiles.length > 0) {
        // Upload each file
        for (const selectedFile of selectedFiles) {
          try {
            // Get presigned URL
            const presignResponse = await getPresignUrlMutation.mutateAsync({
              fileName: selectedFile.file.name,
              contentType: selectedFile.file.type,
            });

            // Upload file to presigned URL
            await uploadFileToUrl(presignResponse.uploadUrl, selectedFile.file);

            // Store the public URL
            uploadedUrls.push(presignResponse.publicUrl);
          } catch (uploadError) {
            console.error(`Failed to upload ${selectedFile.name}:`, uploadError);
            throw new Error(`Failed to upload ${selectedFile.name}`);
          }
        }
      }

      // Combine uploaded file URLs with image URLs
      const allAttachments = [...uploadedUrls, ...attachedImageUrls];

      // Submit feedback with uploaded URLs
      await submitFeedbackMutation.mutateAsync({
        feedbackType,
        description: feedbackDescription,
        attachments: allAttachments,
      });

      // Reset form on success
      setFeedbackDescription("");
      setSelectedFiles([]);
      setAttachedImageUrls([]);

            // Show success message
            addToast(
                'Feedback submitted successfully! Thank you for your input.',
                'success',
                5000
            )
        } catch (error) {
            console.error('Failed to submit feedback:', error)
            addToast('Failed to submit feedback. Please try again.', 'error')
        } finally {
            setIsSubmitting(false)
        }
    }

  const handleAttachFileClick = () => {
    fileInputRef.current?.click();
  };

  const handleAttachImageClick = () => {
    imageUpload.triggerUpload();
  };

  const removeAttachedImage = useCallback((urlToRemove: string) => {
    setAttachedImageUrls(prev => prev.filter(url => url !== urlToRemove));
  }, []);

    return (
        <div
            className={classNames(
                'p-4',
                rootClassNames
            )}
        >
            {!asChildren && <PageHeader title="Feedback" />}
            <form
                onSubmit={handleSubmit}
                className="mt-4 flex h-[calc(100dvh-100px)] flex-col justify-between gap-4"
            >
                <div className="flex flex-col gap-4">
                    {/* Feedback Type */}
                    <div className="w-full">
                        <label className="text-14 text-black-48 mb-2 block font-normal">
                            Type of feedback
                        </label>
                        <div className="relative">
                            <button
                                type="button"
                                className="border-black-12 flex w-full items-center justify-between rounded-xl border bg-white px-4 py-[14px]"
                                onClick={() =>
                                    setIsFeedbackTypeBottomSheetOpen(true)
                                }
                            >
                                <span className="text-base font-medium">
                                    {feedbackType}
                                </span>
                                <ChevronDown className="text-gray-500" />
                            </button>
                        </div>
                    </div>

                    {/* Description */}
                    <CustomTextarea
                        label="Description"
                        value={feedbackDescription}
                        onChange={(e) => setFeedbackDescription(e.target.value)}
                        placeholder="Enter your feedback here"
                        maxLength={500}
                        className="min-h-[150px]"
                        showCounter={false}
                    />

          <div className="flex flex-col">
            {/* Selected Files and Images */}
            {(selectedFiles.length > 0 || attachedImageUrls.length > 0) && (
              <div className="mt-2">
                {/* Non-image files */}
                {selectedFiles.map((selectedFile) => (
                  <div
                    key={selectedFile.id}
                    className="flex items-center justify-between p-4 bg-black-4 rounded-2xl mb-2"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center">
                        <Document className="w-5 h-5 text-gray-600" />
                      </div>
                      <div>
                        <p className="text-14 font-medium">
                          {selectedFile.name.slice(0, 20) + "..."}
                        </p>
                        <p className="text-13 text-black-48">
                          {selectedFile.type.split("/")[0].toUpperCase()} file /
                          Selected
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(selectedFile.id)}
                      className="text-gray-500 cursor-pointer hover:text-red-500"
                    >
                      <TrashCan className="w-5 h-5" />
                    </button>
                  </div>
                ))}

                {/* Attached images */}
                {attachedImageUrls.map((url, index) => (
                  <div
                    key={url}
                    className="flex items-center justify-between p-4 bg-black-4 rounded-2xl mb-2"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 bg-white rounded-xl overflow-hidden">
                        <img
                          src={url}
                          alt={`Attachment ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div>
                        <p className="text-14 font-medium">
                          Image attachment {index + 1}
                        </p>
                        <p className="text-13 text-black-48">
                          IMAGE file / Uploaded
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeAttachedImage(url)}
                      className="text-gray-500 cursor-pointer hover:text-red-500"
                    >
                      <TrashCan className="w-5 h-5" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Hidden file input */}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              className="hidden"
              multiple
            />

            {/* Attach Buttons */}
            <div className="flex gap-2 mb-4">
              <Button
                type="button"
                onClick={handleAttachImageClick}
                disabled={imageUpload.isUploading}
                variant="outline"
                leftIcon={<Attachment className="w-5 h-5" />}
                className="flex-1"
              >
                {imageUpload.isUploading ? 'Uploading...' : 'Attach Image'}
              </Button>
              <Button
                type="button"
                onClick={handleAttachFileClick}
                variant="outline"
                leftIcon={<Attachment className="w-5 h-5" />}
                className="flex-1"
              >
                Attach File
              </Button>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <Button
          type="submit"
          variant="solid"
          className="mt-auto bg-black"
          disabled={isSubmitting || !feedbackDescription.trim() || imageUpload.isUploading}
        >
          {isSubmitting ? "Submitting..." :
           imageUpload.isUploading ? "Uploading image..." :
           "Submit"}
        </Button>
      </form>

      <div className="fixed">
        <FeedbackTypeBottomSheet
          open={isFeedbackTypeBottomSheetOpen}
          onClose={() => setIsFeedbackTypeBottomSheetOpen(false)}
          onSelect={(type) => setFeedbackType(type)}
          selectedType={feedbackType}
        />

        {/* Image Upload Bottom Sheets */}
        <ChooseFileBottomSheet {...imageUpload.filePickerProps} />
        <ImageCropperBottomSheet {...imageUpload.cropperProps} />
      </div>
    </div>
  );
};
